import {
	Accordion,
	AccordionButton,
	AccordionItem,
	AccordionPanel,
	Box,
	Popover,
	PopoverArrow,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Spacer,
} from '@chakra-ui/react';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { ChevronUpSolid } from '../../../design-system/icon';
import { Heading } from '../../../design-system/typography';
import { useLocalStorage } from '../../../lib/storage';
import { Bizy } from '../../bizy';
import { ErrorBoundary } from '../../error/error-boundary';
import { ReactComponent as AiSparkle } from './ai-sparkle.svg';
import { DraftAssistCard, DraftAssistCardProps } from './components/draft-assist-card';
import { Dimension, ScoreBoxAccordionContent } from './scorebox-components';

type ScoreBoxAccordionProps = {
	dimensions: Dimension[];
	isLoading: boolean;
	text: string;
	draftAssist?: DraftAssistCardProps;
	hasUnmetDimension?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
};

export const ScoreBoxAccordion: FC<ScoreBoxAccordionProps> = ({ dimensions, isLoading, text, draftAssist, hasUnmetDimension, onOpenChange }) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'gratitudeGauge' });
	const isDraftAssistEnabled = useFeatureFlag('bizy-draft-assist');
	// When draft assist is off: restore open state across sessions (original UX).
	// When draft assist is on: always start closed so the LLM only fires on explicit
	// user intent. Session state resets on each modal open — no localStorage restore.
	const [storedIsOpen, setStoredIsOpen] = useLocalStorage('gratitude-gauge-open:mobile', true);
	const [sessionIsOpen, setSessionIsOpen] = useState(false);
	const isOpen = isDraftAssistEnabled ? sessionIsOpen : storedIsOpen;
	const setIsOpen = isDraftAssistEnabled ? setSessionIsOpen : setStoredIsOpen;
	const { track } = useAnalytics();
	const matches = dimensions.filter((d) => d.matches).length;

	const bizyState = useMemo(() => (isLoading ? 'thinking' : 'curious'), [isLoading]);

	const headingText = useMemo(() => {
		return isDraftAssistEnabled ? t('bizyTitle') : t('heading');
	}, [isDraftAssistEnabled, t]);

	return (
		<ErrorBoundary>
			<Accordion
				allowToggle
				index={isOpen ? 0 : -1}
				onChange={(index) => {
					const nextIsOpen = index === 0;

					if (nextIsOpen) {
						track(AnalyticsEvent.GratitudeGauge.Opened, { type: 'accordion' });
					} else {
						track(AnalyticsEvent.GratitudeGauge.Closed, { type: 'accordion' });
					}

					setIsOpen(nextIsOpen);
					onOpenChange?.(nextIsOpen);
				}}
			>
				<AccordionItem borderBottom="none">
					{({ isExpanded }) => (
						<>
							<AccordionButton
								bgColor={isDraftAssistEnabled ? undefined : 'purple.400'}
								color={isDraftAssistEnabled ? 'text.default' : 'text.onColor'}
								columnGap={2}
								justifyContent="center"
								py={{ base: 3, sm: 2 }}
								_hover={{ bgColor: isDraftAssistEnabled ? undefined : 'purple.500' }}
							>
								{isDraftAssistEnabled ? (
									<Popover isOpen={hasUnmetDimension && !isExpanded} placement="top" closeOnBlur={false} isLazy>
										<PopoverTrigger>
											<Box as="span" display="inline-block" flexShrink={0}>
												<Bizy state={bizyState} size="md" />
											</Box>
										</PopoverTrigger>
										<PopoverContent width="auto" maxW="160px" pointerEvents="none">
											<PopoverArrow />
											<PopoverBody fontSize="xs" textAlign="center" px={3} py={2}>
												{t('draftAssistHint')}
											</PopoverBody>
										</PopoverContent>
									</Popover>
								) : (
									<AiSparkle />
								)}
								<Heading>{headingText}</Heading>
								<Box color="text.onColor" fontSize="sm" fontWeight="medium" borderRadius="md" px={2} bg="purple.300" letterSpacing="wider">
									{matches}/{dimensions.length}
								</Box>
								<Spacer />
								<ChevronUpSolid transitionProperty="transform" transitionDuration="fast" transform={isExpanded ? 'none' : 'rotate(180deg)'} />
							</AccordionButton>

							<AccordionPanel p={3}>
								{draftAssist && (
									<DraftAssistCard
										suggestion={draftAssist.suggestion}
										dimensionKey={draftAssist.dimensionKey}
										isLoading={draftAssist.isLoading}
										onAdd={draftAssist.onAdd}
										onDismiss={draftAssist.onDismiss}
									/>
								)}
								<ScoreBoxAccordionContent dimensions={dimensions} isLoading={isLoading} text={text} />
							</AccordionPanel>
						</>
					)}
				</AccordionItem>
			</Accordion>
		</ErrorBoundary>
	);
};
